
import { supabase } from "@/integrations/supabase/client";

interface EdgeFunctionOptions {
  priority?: 'high' | 'normal' | 'low';
  timeout?: number;
  signal?: AbortSignal;
}

export const invokeEdgeFunction = async (
  functionName: string,
  payload?: any,
  customHeaders?: Record<string, string>,
  options: EdgeFunctionOptions = { priority: 'normal' }
) => {
  try {
    
    // Handle SSO callback which uses URL parameters instead of body
    if (functionName.includes('discourse-sso') && payload) {
      // Construct the function URL with project reference
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: payload,
      });
      
      if (error) {
        console.error(`❌ [edgeFunctionUtils] Error invoking ${functionName}:`, error);
        // Check for structured error data
        if (data && (data.error || data.message)) {
          return { data: null, error: data };
        }
        throw error;
      }
      
      // If the response contains a redirect URL, follow it
      if (data && data.redirectUrl) {
        window.location.href = data.redirectUrl;
        return { data: { redirecting: true }, error: null };
      }
      
      return { data, error: null };
    }
    
    // For extract-metrics function, ensure both version and quarter are properly passed
    if (functionName === 'extract-metrics' && payload) {
      const updatedPayload = { ...payload };
      
      // Make sure both quarter and version are preserved in the payload
      if (updatedPayload.quarter && !updatedPayload.version) {
        console.log(`Using quarter ${updatedPayload.quarter} as version since version is missing`);
        updatedPayload.version = updatedPayload.quarter;
      }
      
      console.log(`Calling ${functionName} with payload:`, updatedPayload);
      
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: updatedPayload,
      });

      if (error) {
        console.error(`❌ [edgeFunctionUtils] Error invoking ${functionName}:`, error);
        // Check for structured error data
        if (data && (data.error || data.message)) {
          return { data: null, error: data };
        }
        throw error;
      }
      
      // Log job ID if available
      if (data && data.jobId) {
        console.log(`✅ [edgeFunctionUtils] ${functionName} job started with ID: ${data.jobId}`);
      }

      return { data, error: null };
    }
    
    // Handle get-company-details function specifically
    if (functionName === 'get-company-details' && payload) {
      // For low priority requests (preloading), we can use a longer timeout
      const timeout = options.priority === 'low' ? 30000 : (options.priority === 'high' ? 10000 : (options.timeout || 15000));
      
      // Create an AbortController for this request if one wasn't provided
      const controller = options.signal ? { signal: options.signal } : new AbortController();
      let timeoutId: number | undefined;
      
      // Only set up our own timeout if we created the controller
      if (!options.signal) {
        timeoutId = setTimeout(() => controller.abort(), timeout);
        console.log(`⏱️ [edgeFunctionUtils] Setting timeout of ${timeout}ms for ${functionName}`);
      }
      
      try {
        console.log(`🔄 [edgeFunctionUtils] Invoking ${functionName} with priority: ${options.priority || 'normal'}`);
        
        const startTime = Date.now();
        const { data, error } = await supabase.functions.invoke(functionName, {
          body: { ...payload, _priority: options.priority }, // Pass priority to edge function
          signal: controller.signal
        });
        
        const duration = Date.now() - startTime;
        console.log(`✅ [edgeFunctionUtils] ${functionName} completed in ${duration}ms`);
        
        // Clear the timeout if we set one
        if (timeoutId) clearTimeout(timeoutId);
        
        if (error) {
          throw error;
        }
        
        // For backward compatibility, if data contains company and events directly
        if (data && data.company) {
          return data; // Return the data object directly as it already has company and events
        }
        
        return { data, error: null };
      } catch (error) {
        // Clear the timeout if we set one
        if (timeoutId) clearTimeout(timeoutId);
        
        // Check if this was an abort error
        if (error.name === 'AbortError') {
          console.error(`⏱️ [edgeFunctionUtils] Request to ${functionName} timed out or was aborted`);
          throw new Error(`Request to ${functionName} timed out or was aborted`);
        }
        
        throw error;
      }
    }
    
    // Handle get-image function with height parameter
    if (functionName === 'get-image' && payload) {
      console.log(`Calling ${functionName} with payload:`, payload);
      
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: payload,
      });
      
      if (error) {
        console.error(`❌ [edgeFunctionUtils] Error invoking ${functionName}:`, error);
        // Check for structured error data
        if (data && (data.error || data.message)) {
          return { data: null, error: data };
        }
        throw error;
      }
      
      return { data, error: null };
    }
    
    // Handle get-crop function
    if (functionName === 'get-crop' && payload) {
      console.log(`Calling ${functionName} with payload:`, payload);

      const { data, error } = await supabase.functions.invoke(functionName, {
        body: payload,
      });

      if (error) {
        console.error(`❌ [edgeFunctionUtils] Error invoking ${functionName}:`, error);
        // Check for structured error data
        if (data && (data.error || data.message)) {
          return { data: null, error: data };
        }
        throw error;
      }

      return { data, error: null };
    }

    // Handle diff-chat functions with extended timeout
    if ((functionName === 'diff-chat/start' || functionName === 'diff-chat/message') && payload) {
      console.log(`Calling ${functionName} with payload:`, payload);

      // Use a 3 minute timeout for analyze-diffs (edge functions timeout at 150s)
      const timeout = options.timeout || 180000; // 3 minutes
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      console.log(`⏱️ [edgeFunctionUtils] Setting timeout of ${timeout}ms for ${functionName}`);

      try {
        const startTime = Date.now();
        const { data, error } = await supabase.functions.invoke(functionName, {
          body: payload,
          signal: controller.signal
        });

        const duration = Date.now() - startTime;
        console.log(`✅ [edgeFunctionUtils] ${functionName} completed in ${duration}ms`);

        clearTimeout(timeoutId);

        if (error) {
          console.error(`❌ [edgeFunctionUtils] Error invoking ${functionName}:`, error);
          // Check for structured error data
          if (data && (data.error || data.message)) {
            return { data: null, error: data };
          }
          throw error;
        }

        return { data, error: null };
      } catch (error) {
        clearTimeout(timeoutId);

        // Check if this was an abort error (timeout)
        if (error.name === 'AbortError') {
          console.error(`⏱️ [edgeFunctionUtils] Request to ${functionName} timed out`);
          return {
            data: null,
            error: {
              message: 'The analysis is taking longer than expected. This might be due to analyzing a large number of diffs. Please try with a smaller time period or add more filters.',
              isTimeout: true
            }
          };
        }

        throw error;
      }
    }
    
    // Normal function invocation
    const invokeOptions: any = { body: payload };
    if (customHeaders) {
      invokeOptions.headers = customHeaders;
    }
    
    const { data, error } = await supabase.functions.invoke(functionName, invokeOptions);

    if (error) {
      // Check if there's structured error data in the response
      // When edge functions return 4xx/5xx with JSON, the data might contain the actual error info
      if (data && (data.error || data.message)) {
        console.log(`📋 [edgeFunctionUtils] Structured error response from ${functionName}:`, data);
        // Return the structured error data instead of the generic Supabase error
        return { data: null, error: data };
      }
      
      // For backward compatibility, still throw the original error if no structured response
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

// Helper function to get company slugs by category
export const getCompanySlugsByCategory = async (categories: string[] = ['All']) => {
  try {
    const { data: companies, error } = await supabase
      .from('companies')
      .select('slug, category');
    
    if (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
    
    if (!companies) {
      console.error('No companies returned');
      return [];
    }
    
    // Fix TypeScript issue - ensure we have proper type safety by asserting the shape
    const typedCompanies = companies as Array<{ slug: string; category: string }>;
    
    // Filter companies by category if needed
    const filteredCompanies = categories.includes('All')
      ? typedCompanies
      : typedCompanies.filter(company => categories.includes(company.category));
    
    return filteredCompanies.map(company => company.slug);
  } catch (error) {
    console.error('Error getting company slugs:', error);
    return [];
  }
};

// Helper to get an image from Cloudinary using the get-image function
export const getCloudinaryImage = async (
  slug: string, 
  options: { version?: string; quarter?: string; height?: number } = {}
) => {
  try {
    const { data, error } = await invokeEdgeFunction('get-image', {
      slug,
      version: options.version,
      quarter: options.quarter,
      height: options.height
    });
    
    if (error) {
      console.error('Error getting Cloudinary image:', error);
      throw error;
    }
    
    return {
      imageUrl: data.imageUrl,
      width: data.width,
      height: data.height
    };
  } catch (error) {
    console.error('Failed to get Cloudinary image:', error);
    return null;
  }
};

// Helper to get the status of a job
export const getJobStatus = async (jobId: string) => {
  try {
    const { data, error } = await invokeEdgeFunction('get-job-status', {
      jobId
    });
    
    if (error) {
      console.error('Error getting job status:', error);
      throw error;
    }
    
    return data.job;
  } catch (error) {
    console.error('Failed to get job status:', error);
    return null;
  }
};

// Helper to create and enqueue a task using task-enqueue edge function
export const createAndEnqueueTask = async (
  taskType: string,
  slug: string,
  options: {
    version?: string;
    quarter?: string;
    force?: boolean;
    useAI?: boolean;
    [key: string]: any; // Allow additional parameters
  } = {}
) => {
  try {
    console.log(`Creating and enqueueing task: ${taskType} for slug: ${slug}`, options);
    
    // Prepare the payload for task-enqueue
    const payload = {
      task_type: taskType,
      slug,
      ...options // Include all additional parameters
    };
    
    const { data, error } = await supabase.functions.invoke('task-enqueue', {
      body: payload
    });
    
    if (error) {
      console.error('Error creating and enqueueing task:', error);
      throw error;
    }
    
    console.log('Task created and enqueued successfully:', data);
    return { data, error: null };
  } catch (error) {
    console.error('Failed to create and enqueue task:', error);
    return { data: null, error };
  }
};
